import {NotificationCenterService_GetNotificationsV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/notification_center/v1/service.grpc';
import {CustomerSession} from '@aboutyou/grpc-clients/tadarida/aysa_api/shared/v1/session.types';
import {useGrpcRequestMutation} from '@aboutyou/data-fetcher';
import {useIsConsentBannerAccepted} from '@aboutyou/storage';
import {
  useBasketToken,
  useFullSession,
  useIsWishlistLoaded,
  useWishlistProductIdsWithSizeIds,
} from '@aboutyou/user';
import {useTargetGroupIdentifier} from '../../shared-deprecated/useTargetGroupIdentifier';
import {useSelectedDiscountRequestParameter} from '../Campaigns/SelectedDiscounts/useSelectedDiscount';
import {GetNotificationsV2Response} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/notification_center/v1/service.types';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSuspenseSafeHydrationDone} from '@aboutyou/lazy-imports';

export interface GetUserNotificationsInterface {
  session: CustomerSession;
}

export const useGetNotificationsCallback = () => {
  const [notificationsData, setNotificationsData] = useState<
    GetNotificationsV2Response | undefined
  >(undefined);

  const fullSession = useFullSession();
  const targetGroup = useTargetGroupIdentifier();
  const {token: basketToken, updateBasketToken} = useBasketToken();
  const wishlistLoaded = useIsWishlistLoaded();
  const wishlistItems = useWishlistProductIdsWithSizeIds();
  const selectedDiscount = useSelectedDiscountRequestParameter();
  const isConsentBannerAccepted = useIsConsentBannerAccepted();
  const isHydrationDone = useSuspenseSafeHydrationDone();

  const isReadyForInitialFetch = Boolean(
    fullSession && basketToken && wishlistLoaded,
  );
  const isInitiallyFetched = useRef(false);

  const mutation = useGrpcRequestMutation(
    NotificationCenterService_GetNotificationsV2,
    {
      disabled: !fullSession,
    },
  );

  const fetchNotifications = useCallback(async () => {
    return mutation
      .mutateAsync({
        session: fullSession!,
        targetGroup,
        selectedDiscount,
        basketToken,
        wishlistItems,
        enableConsentCompliantNotifications: isConsentBannerAccepted,
      })
      .then(response => {
        updateBasketToken(response.basketToken);
        setNotificationsData(response);

        return response;
      });
  }, [
    isHydrationDone
      ? [
          basketToken,
          fullSession,
          isConsentBannerAccepted,
          mutation,
          selectedDiscount,
          targetGroup,
          updateBasketToken,
          wishlistItems,
        ]
      : false,
  ]);

  // we have to fetch notifications one on pageload to show the notifications count in the header
  // and then everytime the user clicks the bell icon to get the most updated notifications again
  useEffect(() => {
    if (isInitiallyFetched.current || !isHydrationDone) {
      return;
    }

    if (isReadyForInitialFetch) {
      isInitiallyFetched.current = true;
      fetchNotifications()?.then(setNotificationsData);
    }
  }, [fetchNotifications, isHydrationDone, isReadyForInitialFetch]);

  return {notificationsData, fetchNotifications};
};
